import React, { useEffect, useState } from "react";
import { Grid, Drawer, Typography, IconButton, Box, Stack, Tab } from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import WorklistSettings from "../../organisms/CollectionSettings";
import theme from "../../../theme/Theme";
import cancel from "../../../../public/assets/icons/cancel.svg";
import { WidgetConstants, policiesContext } from "../../../utils/common";
import { QARuleForm } from "../../organisms/QARuleForm";
import RubricsPanel from "../../organisms/CollectionSettings/RubricsPanel";
import CodeWorkflowsPanel from "../../organisms/CollectionSettings/CodeWorkflowsPanel";
import AnswerSetsPanel from "../../organisms/CollectionSettings/AnswerSetsPanel";
import QuestionsPanel from "../../organisms/CollectionSettings/QuestionsPanel";
import ResolverBuilderPanel from "../../organisms/CollectionSettings/ResolverBuilderPanel";
import { includes } from "lodash";

interface IProps {
    isOpen: boolean;
    onClose: () => void;
    client: any;
}

const QualityAssessmentSettings: React.FC<IProps> = ({ isOpen, onClose, client }) => {
    const { roles } = React.useContext(policiesContext);
    const hasRubricAdminRole = includes(roles, "rubric_admin");
    const [showDetails, setShowDetails] = useState(false);
    const [directoryType, setDirectoryType] = useState("cqa_samples");
    const [selected, setSelected] = useState("");
    const [activeTab, setActiveTab] = useState<"projects" | "rubrics" | "codeWorkflows" | "questions" | "answerSets" | "resolver">(
        "projects"
    );
    const [rubricPath, setRubricPath] = useState("Workflow Collections");
    const [codeWorkflowPath, setCodeWorkflowPath] = useState("Workflows");
    const [answerSetPath, setAnswerSetPath] = useState("Answer Sets");
    const [questionPath, setQuestionPath] = useState("Questions");

    const showForm = (profile: any, show: boolean) => {
        setSelected(profile);
        setShowDetails(show);
    };

    useEffect(() => {
        // If user doesn't have rubric_admin role and is on a restricted tab, switch to projects
        if (!hasRubricAdminRole && (activeTab === "rubrics" || activeTab === "codeWorkflows" || activeTab === "questions" || activeTab === "answerSets" || activeTab === "resolver")) {
            setActiveTab("projects");
        }
    }, [hasRubricAdminRole, activeTab]);

    useEffect(() => {
        if (activeTab === "rubrics" && showDetails) {
            setShowDetails(false);
        }
        if (activeTab !== "rubrics") {
            setRubricPath("Workflow Collections");
        }
        if (activeTab !== "codeWorkflows") {
            setCodeWorkflowPath("Workflows");
        }
        if (activeTab !== "answerSets") {
            setAnswerSetPath("Answer Sets");
        }
        if (activeTab !== "questions") {
            setQuestionPath("Questions");
        }
    }, [activeTab, showDetails]);

    const renderProjects = () => (
        <>
            {showDetails ? (
                <QARuleForm
                    directoryType={directoryType}
                    setShowDetails={setShowDetails}
                    client={client}
                    selected={selected}
                />
            ) : (
                <WorklistSettings
                    DirectoryType={directoryType}
                    client={client}
                    setShowDetails={setShowDetails}
                    showForm={showForm}
                />
            )}
        </>
    );
    return (
        <Drawer
            ModalProps={{
                container: () => {
                    return document.getElementById("fullscreenPortal") as HTMLElement;
                },
                style: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                },
            }}
            PaperProps={{ style: { position: "absolute", margin: "20px" } }}
            SlideProps={{
                onExiting: (node) => {
                    node.style.webkitTransform = "scaleX(0)";
                    node.style.transform = "scaleX(0)";
                    node.style.transformOrigin = "top left ";
                },
            }}
            anchor="top"
            open={isOpen}
            onClose={onClose}
        >
            <Grid container>
                <Grid item container margin={"20px"}>
                    <Grid item xs={6}>
                        <Typography variant="h2">
                            {WidgetConstants.QUALITY_ASSESSMENT}
                            <span> / </span>
                            <span style={{ color: theme.palette.artisticTextHigh.main }}>Manage</span>
                            {activeTab === "rubrics" && rubricPath ? (
                                <>
                                    <span> / </span>
                                    <span>{rubricPath}</span>
                                </>
                            ) : null}
                            {activeTab === "codeWorkflows" && codeWorkflowPath ? (
                                <>
                                    <span> / </span>
                                    <span>{codeWorkflowPath}</span>
                                </>
                            ) : null}
                            {activeTab === "questions" && questionPath ? (
                                <>
                                    <span> / </span>
                                    <span>{questionPath}</span>
                                </>
                            ) : null}
                            {activeTab === "answerSets" && answerSetPath ? (
                                <>
                                    <span> / </span>
                                    <span>{answerSetPath}</span>
                                </>
                            ) : null}
                        </Typography>
                    </Grid>
                    <Grid item xs={6} display={"flex"} justifyContent={"flex-end"}>
                        <IconButton onClick={() => onClose()}>
                            <img
                                src={cancel}
                                alt="close quality assessment settings"
                                style={{ height: "20px" }}
                            />
                        </IconButton>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <TabContext value={activeTab}>
                        <Box
                            sx={{
                                display: "flex",
                                borderBottom: "1px solid lightgray",
                                px: 3,
                            }}
                        >
                            <TabList
                                onChange={(_event, value) => {
                                    setActiveTab(value);
                                }}
                            >
                                <Tab label={<Typography variant="body1">Projects</Typography>} value="projects" />
                                {hasRubricAdminRole && (
                                    <Tab label={<Typography variant="body1">Workflow Collections</Typography>} value="rubrics" />
                                )}
                                {hasRubricAdminRole && (
                                    <Tab
                                        label={<Typography variant="body1">Workflows</Typography>}
                                        value="codeWorkflows"
                                    />
                                )}
                                {hasRubricAdminRole && (
                                    <Tab label={<Typography variant="body1">Questions</Typography>} value="questions" />
                                )}
                                {hasRubricAdminRole && (
                                    <Tab label={<Typography variant="body1">Answer Sets</Typography>} value="answerSets" />
                                )}
                                {hasRubricAdminRole && (
                                    <Tab label={<Typography variant="body1">Resolver</Typography>} value="resolver" />
                                )}
                            </TabList>
                        </Box>
                        <TabPanel value="projects" sx={{ px: 0 }}>
                            <Box sx={{ padding: "20px" }}>
                                <Stack spacing={2}>{renderProjects()}</Stack>
                            </Box>
                        </TabPanel>
                        {hasRubricAdminRole && (
                            <TabPanel value="rubrics" sx={{ px: 0 }}>
                                <Box sx={{ padding: "20px" }}>
                                    <RubricsPanel onPathChange={setRubricPath} />
                                </Box>
                            </TabPanel>
                        )}
                        {hasRubricAdminRole && (
                            <TabPanel value="codeWorkflows" sx={{ px: 0 }}>
                                <Box sx={{ padding: "20px" }}>
                                    <CodeWorkflowsPanel onPathChange={setCodeWorkflowPath} />
                                </Box>
                            </TabPanel>
                        )}
                        {hasRubricAdminRole && (
                            <TabPanel value="questions" sx={{ px: 0 }}>
                                <Box sx={{ padding: "20px" }}>
                                    <QuestionsPanel
                                        onPathChange={setQuestionPath}
                                    />
                                </Box>
                            </TabPanel>
                        )}
                        {hasRubricAdminRole && (
                            <TabPanel value="answerSets" sx={{ px: 0 }}>
                                <Box sx={{ padding: "20px" }}>
                                    <AnswerSetsPanel onPathChange={setAnswerSetPath} />
                                </Box>
                            </TabPanel>
                        )}
                        {hasRubricAdminRole && (
                            <TabPanel value="resolver" sx={{ px: 0 }}>
                                <Box sx={{ padding: "20px" }}>
                                    <ResolverBuilderPanel />
                                </Box>
                            </TabPanel>
                        )}
                    </TabContext>
                </Grid>
            </Grid>
        </Drawer>
    );
};

export default QualityAssessmentSettings;
